import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContextBackend';
import api from '../api/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Button, TimezoneSelector, Switch, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label, ConfirmationDialog } from '@t-one-internal/design-system';
import { Building2, Globe, Mail, Phone, Clock, Loader2, Plus, Edit, Trash2, Database, AlertTriangle } from 'lucide-react';
import AccountLogoManager from './AccountLogoManager';
import { domainService, type Domain } from '../api/domainService';
import { getAccountSettings, updateWelcomeEmailSetting, deleteDemoData } from '../api/account';

interface OrganizationData {
  companyName: string;
  domain: string;
  logoUrl: string;
  email: string;
  phone: string;
  timezone: string;
  minAge: number;
}

const ProfilePage: React.FC = () => {
  const { account, updateAccount } = useAuth();
  const [formData, setFormData] = useState<OrganizationData>({
    companyName: '',
    domain: '',
    logoUrl: '',
    email: '',
    phone: '',
    timezone: 'America/New_York',
    minAge: 18,
  });
  const [isSavingTimezone, setIsSavingTimezone] = useState(false);
  const [timezoneError, setTimezoneError] = useState<string | null>(null);
  const [timezoneSaved, setTimezoneSaved] = useState(false);
  const [isSavingMinAge, setIsSavingMinAge] = useState(false);
  const [minAgeError, setMinAgeError] = useState<string | null>(null);
  const [minAgeSaved, setMinAgeSaved] = useState(false);
  const [domains, setDomains] = useState<Domain[]>([]);
  const [isLoadingDomains, setIsLoadingDomains] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [editingDomain, setEditingDomain] = useState<Domain | null>(null);
  const [newDomain, setNewDomain] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState<boolean>(true);
  const [isLoadingWelcomeEmail, setIsLoadingWelcomeEmail] = useState(false);
  const [isSavingWelcomeEmail, setIsSavingWelcomeEmail] = useState(false);
  const [welcomeEmailError, setWelcomeEmailError] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [domainToDelete, setDomainToDelete] = useState<{ id: string; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isDeletingDemoData, setIsDeletingDemoData] = useState(false);
  const [isDeleteDemoDialogOpen, setIsDeleteDemoDialogOpen] = useState(false);
  const [demoDataDeleteSuccess, setDemoDataDeleteSuccess] = useState<string | null>(null);
  const [demoDataDeleteError, setDemoDataDeleteError] = useState<string | null>(null);
  const [hasDemoData, setHasDemoData] = useState<boolean>(false);

  // Initialize form data from account
  useEffect(() => {
    if (account) {
      const initialData: OrganizationData = {
        companyName: account.name || '',
        domain: account.domain || '',
        logoUrl: '',
        email: '',
        phone: '',
        timezone: account.timezone || 'America/New_York',
        minAge: account.min_age || 18,
      };
      setFormData(initialData);
    }
  }, [account]);

  const handleTimezoneChange = (timezone: string) => {
    setFormData((prev) => ({ ...prev, timezone }));
    setTimezoneSaved(false);
    setTimezoneError(null);
  };

  const handleSaveTimezone = async () => {
    setIsSavingTimezone(true);
    setTimezoneError(null);
    setTimezoneSaved(false);

    try {
      await api.put('/account/settings', { timezone: formData.timezone });
      updateAccount({ timezone: formData.timezone });
      setTimezoneSaved(true);
      setTimeout(() => setTimezoneSaved(false), 3000);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to save timezone';
      setTimezoneError(errorMessage);
    } finally {
      setIsSavingTimezone(false);
    }
  };

  const handleMinAgeChange = (value: string) => {
    const parsedValue = Number(value);
    if (Number.isNaN(parsedValue)) {
      return;
    }
    setFormData((prev) => ({ ...prev, minAge: parsedValue }));
    setMinAgeSaved(false);
    setMinAgeError(null);
  };

  const handleSaveMinAge = async () => {
    setIsSavingMinAge(true);
    setMinAgeError(null);
    setMinAgeSaved(false);

    if (!Number.isInteger(formData.minAge) || formData.minAge < 14 || formData.minAge > 30) {
      setMinAgeError('Minimum age must be an integer between 14 and 30');
      setIsSavingMinAge(false);
      return;
    }

    try {
      await api.put('/account/settings/age', { min_age: formData.minAge });
      updateAccount({ min_age: formData.minAge });
      setMinAgeSaved(true);
      setTimeout(() => setMinAgeSaved(false), 3000);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to save minimum age';
      setMinAgeError(errorMessage);
    } finally {
      setIsSavingMinAge(false);
    }
  };

  useEffect(() => {
    fetchDomains();
    fetchAccountSettings();
  }, []);

  const fetchAccountSettings = async () => {
    setIsLoadingWelcomeEmail(true);
    setWelcomeEmailError(null);
    try {
      const settings = await getAccountSettings();
      setSendWelcomeEmail(settings.send_welcome_email ?? true);
      setHasDemoData(settings.has_demo_data ?? false);
      setFormData(prev => ({
        ...prev,
        timezone: settings.timezone || prev.timezone,
        minAge: settings.min_age ?? prev.minAge,
      }));
    } catch (err: any) {
      console.error('Error fetching account settings:', err);
      setWelcomeEmailError(err.response?.data?.error || 'Failed to load email settings');
    } finally {
      setIsLoadingWelcomeEmail(false);
    }
  };

  const handleWelcomeEmailToggle = async (checked: boolean) => {
    setIsSavingWelcomeEmail(true);
    setWelcomeEmailError(null);

    try {
      const response = await updateWelcomeEmailSetting(checked);
      if (response.success) {
        setSendWelcomeEmail(checked);
      } else {
        setWelcomeEmailError('Failed to update welcome email setting');
      }
    } catch (err: any) {
      console.error('Error updating welcome email setting:', err);
      setWelcomeEmailError(err.response?.data?.error || 'Failed to update welcome email setting');
      setSendWelcomeEmail(!checked);
    } finally {
      setIsSavingWelcomeEmail(false);
    }
  };

  const fetchDomains = async () => {
    setIsLoadingDomains(true);
    setError(null);
    try {
      const domainsList = await domainService.listDomains();

      if (Array.isArray(domainsList)) {
        const validDomains = domainsList.filter(
          (domain) =>
            domain &&
            typeof domain === 'object' &&
            (domain.domain || domain.name)
        );
        setDomains(validDomains);
      } else if (domainsList && typeof domainsList === 'object') {
        console.warn('Domains API returned object instead of array:', domainsList);
        const domainObj = domainsList as Domain;
        if (domainObj.domain || domainObj.name) {
          setDomains([domainObj]);
        } else {
          setDomains([]);
        }
      } else {
        console.warn('Domains list is not an array:', domainsList);
        setDomains([]);
      }
    } catch (err: any) {
      console.error('Error fetching domains:', err);
      const errorMessage = err?.response?.data?.error ||
        err?.message ||
        'Failed to load domains';
      setError(errorMessage);
      setDomains([]);
    } finally {
      setIsLoadingDomains(false);
    }
  };

  const handleAddDomain = () => {
    setNewDomain('');
    setError(null);
    setIsAddDialogOpen(true);
  };

  const handleEditDomain = (domain: Domain) => {
    setEditingDomain(domain);
    setNewDomain(domain.domain || domain.name || '');
    setError(null);
    setIsEditDialogOpen(true);
  };

  const handleSaveDomain = async () => {
    if (!newDomain || !newDomain.trim()) {
      setError('Domain is required');
      return;
    }

    const domainPattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    if (!domainPattern.test(newDomain.trim())) {
      setError('Please enter a valid domain name');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      if (editingDomain) {
        const domainId = String(editingDomain.domain_id || editingDomain.id || '');
        if (!domainId) {
          setError('Domain ID is required for update');
          return;
        }
        await domainService.updateDomain(domainId, { name: newDomain.trim() });
      } else {
        await domainService.createDomain({ name: newDomain.trim() });
      }

      await fetchDomains();
      setIsAddDialogOpen(false);
      setIsEditDialogOpen(false);
      setEditingDomain(null);
      setNewDomain('');
    } catch (err: any) {
      console.error('Error saving domain:', err);
      setError(err.response?.data?.error || 'Failed to save domain');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteDomainClick = (domainId: string, domainName: string) => {
    setDomainToDelete({ id: String(domainId), name: domainName });
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!domainToDelete) {
      return;
    }

    setIsDeleting(true);
    try {
      await domainService.deleteDomain(domainToDelete.id);
      await fetchDomains();
      setIsDeleteDialogOpen(false);
      setDomainToDelete(null);
    } catch (err: any) {
      console.error('Error deleting domain:', err);
      const errorMsg = err.response?.data?.error || 'Failed to delete domain';
      setErrorMessage(errorMsg);
      setIsErrorDialogOpen(true);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseDeleteDialog = () => {
    if (!isDeleting) {
      setIsDeleteDialogOpen(false);
      setDomainToDelete(null);
    }
  };

  const handleCloseAddDialog = () => {
    setIsAddDialogOpen(false);
    setNewDomain('');
    setError(null);
  };

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditingDomain(null);
    setNewDomain('');
    setError(null);
  };

  const handleDeleteDemoData = async () => {
    setIsDeletingDemoData(true);
    setDemoDataDeleteError(null);
    setDemoDataDeleteSuccess(null);

    try {
      const response = await deleteDemoData();
      if (response.success) {
        const totalDeleted = response.deleted_items?.reduce((sum, item) => sum + item.rows_affected, 0) || 0;
        setDemoDataDeleteSuccess(`Successfully deleted ${totalDeleted} demo data records`);
        setIsDeleteDemoDialogOpen(false);
        setHasDemoData(false);
        setTimeout(() => setDemoDataDeleteSuccess(null), 5000);
      } else {
        setDemoDataDeleteError('Failed to delete demo data');
      }
    } catch (err: any) {
      console.error('Error deleting demo data:', err);
      setDemoDataDeleteError(err.response?.data?.error || 'Failed to delete demo data');
    } finally {
      setIsDeletingDemoData(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-6 py-6">
        <h1 className="text-2xl font-semibold text-gray-900 mb-4">Organization</h1>
      </div>

      <div className="px-6">
        <div className="max-w-4xl space-y-6">
          {/* Basic Information Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-gray-600" />
                <CardTitle>Basic Information</CardTitle>
              </div>
              <CardDescription>Core details about your organization</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Company Logo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Logo
                </label>
                <AccountLogoManager
                  onLogoChange={(logoUrl: string | null) => {
                    setFormData((prev) => ({ ...prev, logoUrl: logoUrl || '' }));
                  }}
                />
              </div>

              {/* Company Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name
                </label>
                <p className="text-base text-gray-900">{formData.companyName || '-'}</p>
              </div>

              {/* Domain */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Domain
                </label>
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-gray-400" />
                  <p className="text-base text-gray-900">{formData.domain || '-'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-gray-600" />
                <CardTitle>Contact Information</CardTitle>
              </div>
              <CardDescription>How to reach your organization</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <p className="text-base text-gray-900">{formData.email || '-'}</p>
                </div>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone
                </label>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <p className="text-base text-gray-900">{formData.phone || '-'}</p>
                </div>
              </div>

              {/* Domains Table */}
              <div className="mt-6">
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Domains
                  </label>
                  <Button
                    onClick={handleAddDomain}
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add Domain
                  </Button>
                </div>

                {isLoadingDomains ? (
                  <div className="text-center py-8 text-gray-500">
                    Loading domains...
                  </div>
                ) : error && domains.length === 0 ? (
                  <div className="text-center py-8 text-red-500">
                    {error}
                  </div>
                ) : domains.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 border border-gray-200 rounded-md">
                    No domains found. Click "Add Domain" to add one.
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-md overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Domain
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {domains && Array.isArray(domains) && domains.length > 0 ? (
                          domains.map((domain) => {
                            if (!domain || typeof domain !== 'object') {
                              return null;
                            }

                            const domainId = domain.domain_id
                              ? String(domain.domain_id)
                              : (domain.id ? String(domain.id) : '');
                            const domainName = domain.domain || domain.name || '';

                            if (!domainName) {
                              return null;
                            }

                            return (
                              <tr key={domainId || domainName} className="hover:bg-gray-50">
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="flex items-center gap-2">
                                    <Globe className="h-4 w-4 text-gray-400" />
                                    <span className="text-sm text-gray-900">{domainName}</span>
                                  </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                                  <div className="flex items-center justify-end gap-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleEditDomain(domain)}
                                      className="h-8 w-8 p-0"
                                    >
                                      <Edit className="w-4 h-4 text-gray-600" />
                                    </Button>
                                    {domainId && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDeleteDomainClick(domainId, domainName)}
                                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={2} className="px-4 py-8 text-center text-gray-500">
                              No domains available
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Timezone Settings Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-gray-600" />
                <CardTitle>Timezone Settings</CardTitle>
              </div>
              <CardDescription>
                Set your organization's default timezone. This will be used for displaying dates and times across the application.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="max-w-md">
                <TimezoneSelector
                  value={formData.timezone}
                  onChange={handleTimezoneChange}
                  label="Organization Timezone"
                  showAllTimezones
                />
              </div>

              {timezoneError && (
                <p className="text-sm text-red-600">{timezoneError}</p>
              )}

              {timezoneSaved && (
                <p className="text-sm text-green-600">Timezone saved successfully!</p>
              )}

              <div className="pt-2">
                <Button
                  onClick={handleSaveTimezone}
                  disabled={isSavingTimezone}
                >
                  {isSavingTimezone ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Timezone'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Age Validation Settings Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-gray-600" />
                <CardTitle>Age Validation Settings</CardTitle>
              </div>
              <CardDescription>
                Set the minimum allowed employee age for manual employee creation and bulk import validation.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="max-w-md space-y-2">
                <Label htmlFor="min-age-input">Minimum Employee Age</Label>
                <Input
                  id="min-age-input"
                  type="number"
                  min={14}
                  max={30}
                  step={1}
                  value={formData.minAge}
                  onChange={(e) => handleMinAgeChange(e.target.value)}
                />
                <p className="text-xs text-gray-500">Allowed range: 14 to 30</p>
              </div>

              {minAgeError && (
                <p className="text-sm text-red-600">{minAgeError}</p>
              )}

              {minAgeSaved && (
                <p className="text-sm text-green-600">Minimum age saved successfully!</p>
              )}

              <div className="pt-2">
                <Button
                  onClick={handleSaveMinAge}
                  disabled={isSavingMinAge}
                >
                  {isSavingMinAge ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Minimum Age'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Email Settings Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-gray-600" />
                <CardTitle>Email Settings</CardTitle>
              </div>
              <CardDescription>
                Control whether welcome emails are automatically sent to new users
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Send Welcome Emails
                    </label>
                    <p className="text-sm text-gray-500">
                      When enabled, welcome emails will be automatically sent to:
                    </p>
                    <ul className="text-sm text-gray-500 list-disc list-inside mt-2 space-y-1">
                      <li>New users when an account is registered</li>
                      <li>New users when created by an administrator</li>
                    </ul>
                    <p className="text-xs text-gray-400 mt-2">
                      Note: Password reset emails are always sent and are not affected by this setting.
                    </p>
                  </div>
                  <div className="ml-4">
                    {isLoadingWelcomeEmail ? (
                      <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                    ) : (
                      <Switch
                        checked={sendWelcomeEmail}
                        onCheckedChange={handleWelcomeEmailToggle}
                        disabled={isSavingWelcomeEmail}
                      />
                    )}
                  </div>
                </div>

                {welcomeEmailError && (
                  <p className="text-sm text-red-600">{welcomeEmailError}</p>
                )}

                {isSavingWelcomeEmail && (
                  <p className="text-sm text-gray-500 flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Delete Demo Data Section - Only shown if organization has demo data */}
          {hasDemoData && (
            <Card className="border-red-200">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Database className="h-5 w-5 text-red-600" />
                  <CardTitle className="text-red-600">Demo Data Management</CardTitle>
                </div>
                <CardDescription>
                  Remove all demo data from your account. This includes demo users, performance cycles, reviews, goals, surveys, and related data.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-red-800">Warning: This action cannot be undone</p>
                      <p className="text-sm text-red-700 mt-1">
                        Deleting demo data will permanently remove all pre-loaded sample data including:
                      </p>
                      <ul className="text-sm text-red-700 list-disc list-inside mt-2 space-y-1">
                        <li>Demo users (Sarah HR, John Manager, Jane Employee, etc.)</li>
                        <li>Performance cycles and reviews</li>
                        <li>Calibration sessions and data</li>
                        <li>Check-ins and action items</li>
                        <li>PIP records</li>
                        <li>Goals and goal cycles</li>
                        <li>Surveys and responses</li>
                        <li>Talent and IDP data</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {demoDataDeleteSuccess && (
                  <p className="text-sm text-green-600 flex items-center gap-2">
                    ✓ {demoDataDeleteSuccess}
                  </p>
                )}

                {demoDataDeleteError && (
                  <p className="text-sm text-red-600">{demoDataDeleteError}</p>
                )}

                <Button
                  variant="destructive"
                  onClick={() => setIsDeleteDemoDialogOpen(true)}
                  disabled={isDeletingDemoData}
                  className="flex items-center gap-2"
                >
                  {isDeletingDemoData ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4" />
                      Delete All Demo Data
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Add Domain Dialog */}
      <Dialog
        open={isAddDialogOpen}
        onOpenChange={(open: boolean) => {
          if (!open) {
            handleCloseAddDialog();
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Domain</DialogTitle>
            <DialogDescription>
              Enter the domain name you want to add to your organization.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="domain-name">Domain Name</Label>
              <Input
                id="domain-name"
                placeholder="example.com"
                value={newDomain}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setNewDomain(e.target.value);
                  setError(null);
                }}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter' && !isSaving) {
                    handleSaveDomain();
                  }
                }}
                autoFocus
              />
            </div>
            {error && (
              <p className="text-sm text-red-600">{error}</p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCloseAddDialog}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveDomain}
              disabled={isSaving || !newDomain.trim()}
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Add Domain'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Domain Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onOpenChange={(open: boolean) => {
          if (!open) {
            handleCloseEditDialog();
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Domain</DialogTitle>
            <DialogDescription>
              Update the domain name.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-domain-name">Domain Name</Label>
              <Input
                id="edit-domain-name"
                placeholder="example.com"
                value={newDomain}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setNewDomain(e.target.value);
                  setError(null);
                }}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter' && !isSaving) {
                    handleSaveDomain();
                  }
                }}
                autoFocus
              />
            </div>
            {error && (
              <p className="text-sm text-red-600">{error}</p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCloseEditDialog}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveDomain}
              disabled={isSaving || !newDomain.trim()}
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={isDeleteDialogOpen}
        onOpenChange={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        title="Delete Domain"
        description={`Are you sure you want to delete the domain "${domainToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
        isLoading={isDeleting}
      />

      {/* Error Dialog */}
      <Dialog
        open={isErrorDialogOpen}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setIsErrorDialogOpen(false);
            setErrorMessage('');
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Error</DialogTitle>
            <DialogDescription>
              {errorMessage || 'An error occurred'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => {
                setIsErrorDialogOpen(false);
                setErrorMessage('');
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Demo Data Confirmation Dialog */}
      <ConfirmationDialog
        open={isDeleteDemoDialogOpen}
        onOpenChange={(open) => {
          if (!isDeletingDemoData) {
            setIsDeleteDemoDialogOpen(open);
          }
        }}
        onConfirm={handleDeleteDemoData}
        title="Delete All Demo Data"
        description="Are you sure you want to delete all demo data? This will permanently remove all pre-loaded sample data including demo users, performance cycles, reviews, goals, surveys, and related records. This action cannot be undone."
        confirmText="Delete Demo Data"
        cancelText="Cancel"
        variant="destructive"
        isLoading={isDeletingDemoData}
      />
    </div>
  );
};

export default ProfilePage;
